import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAemConfig } from '@as-react/common-components';
import { Carousel, CarouselContent, CarouselNext, CarouselPrevious, CarouselSlot, CarouselSlidePagination, Stack, Box, Grid, preventDragWhilePinchZoomed, useMediaQueryMatchesOnMobile, } from '@yonderland/andes-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { makeGetProductDefaults, makeGetSelectedColorId, makeGetSelectedSku, } from '../../../redux/product/selectors/ProductSelector';
import { LabelsOverlay } from '../../common/labels-overlay/LabelsOverlay';
import { PlayVideoButton } from '../product-media/PlayVideoButton';
import { ProductMediaWishListButton } from '../product-media/ProductMediaWishListButton';
import { useProductMedia } from '../product-media/useProductMedia';
import { PinchAwareProductMediaCarouselItem, ProductMediaCarouselItem } from './ProductMediaCarouselItem';
const INITIAL_SLIDE_INDEX = 0;
const BACKGROUND_LOAD_IMAGES_THRESHOLD = 2;
const INACTIVE_SLIDE_TAP_ZOOM_SUPPRESSION_MS = 300;
const getMediaItemKey = (item, index) => {
    var _a, _b, _c, _d;
    if (item.isVideo) {
        return (_c = (_b = (_a = item.video) === null || _a === void 0 ? void 0 : _a.productVideoDetail) === null || _b === void 0 ? void 0 : _b.url) !== null && _c !== void 0 ? _c : `video-${index}`;
    }
    return (_d = item.image.picture) !== null && _d !== void 0 ? _d : `image-${index}`;
};
export const ProductMediaCarousel = () => {
    const { mediaCollection, alt } = useProductMedia();
    const [activeSlideIndex, setActiveSlideIndex] = useState(INITIAL_SLIDE_INDEX);
    const onPlayVideo = (newActiveSlideIndex) => {
        setActiveSlideIndex(newActiveSlideIndex);
    };
    const isMobile = useMediaQueryMatchesOnMobile();
    const [carouselApi, setCarouselApi] = useState(null);
    const [tapZoomSuppression, setTapZoomSuppression] = useState(null);
    useEffect(() => {
        setActiveSlideIndex(INITIAL_SLIDE_INDEX);
    }, [mediaCollection.length]);
    const scrollToActiveSlide = useCallback(() => {
        if (carouselApi && carouselApi.selectedScrollSnap() !== activeSlideIndex) {
            carouselApi.scrollTo(activeSlideIndex);
        }
    }, [activeSlideIndex, carouselApi]);
    useEffect(() => {
        scrollToActiveSlide();
    }, [activeSlideIndex, carouselApi, scrollToActiveSlide]);
    const onSelect = useCallback(() => {
        if (!carouselApi)
            return;
        const index = carouselApi.selectedScrollSnap();
        if (index !== activeSlideIndex) {
            onPlayVideo(index);
        }
    }, [carouselApi, activeSlideIndex, onPlayVideo]);
    useEffect(() => {
        if (!carouselApi)
            return;
        carouselApi.on('select', onSelect);
        return () => {
            carouselApi.off('select', onSelect);
        };
    }, [carouselApi, onSelect]);
    useEffect(() => {
        if (!tapZoomSuppression) {
            return;
        }
        const delay = Math.max(0, tapZoomSuppression.until - Date.now());
        const timeoutId = window.setTimeout(() => {
            setTapZoomSuppression(currentSuppression => (currentSuppression === null || currentSuppression === void 0 ? void 0 : currentSuppression.index) === tapZoomSuppression.index && currentSuppression.until === tapZoomSuppression.until
                ? null
                : currentSuppression);
        }, delay);
        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [tapZoomSuppression]);
    const handleInactiveSlideActivate = useCallback((index, event) => {
        if (!isMobile || index === activeSlideIndex) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        setTapZoomSuppression({
            index,
            until: Date.now() + INACTIVE_SLIDE_TAP_ZOOM_SUPPRESSION_MS,
        });
        carouselApi === null || carouselApi === void 0 ? void 0 : carouselApi.scrollTo(index);
    }, [activeSlideIndex, carouselApi, isMobile]);
    const renderContent = useCallback((item, index, altCopy) => {
        const imageIndex = item.isVideo
            ? -1
            : mediaCollection.slice(0, index + 1).filter(media => !media.isVideo).length - 1;
        const shouldLoadBackgroundImage = !item.isVideo && imageIndex > BACKGROUND_LOAD_IMAGES_THRESHOLD;
        return (_jsx("div", { onClickCapture: (event) => handleInactiveSlideActivate(index, event), children: _jsx(Box, { display: item.isVideo ? 'block' : 'flex', backgroundColor: "quiet-3", aspectRatio: "2/3", contentAlign: "center", children: item.isVideo ? (_jsx(ProductMediaCarouselItem, Object.assign({}, item, { shouldLoadBackgroundImage: false, preloadLargeImage: false, alt: altCopy, stop: index !== activeSlideIndex }))) : (_jsx(PinchAwareProductMediaCarouselItem, Object.assign({}, item, { api: carouselApi, isActive: index === activeSlideIndex, shouldLoadBackgroundImage: shouldLoadBackgroundImage, alt: altCopy, preloadLargeImage: index === activeSlideIndex, tapZoomEnabled: (tapZoomSuppression === null || tapZoomSuppression === void 0 ? void 0 : tapZoomSuppression.index) !== index || tapZoomSuppression.until <= Date.now(), stop: index !== activeSlideIndex }))) }) }));
    }, [activeSlideIndex, carouselApi, handleInactiveSlideActivate, mediaCollection, tapZoomSuppression]);
    if (!mediaCollection) {
        return null;
    }
    // If there is only one image, we don't need to render the carousel, just the image itself.
    if (mediaCollection.length === 1) {
        return (_jsx(ProductMediaCarouselOverlays, { onPlayVideo: onPlayVideo, children: _jsx(Stack, { justify: "center", align: "center", children: mediaCollection.map((image, index) => renderContent(image, index, alt)) }) }));
    }
    return (_jsx(Carousel, { testId: "product_main_slider", slideSize: "80%", gap: 2, hideOverflow: true, opts: {
            align: 'start',
            containScroll: 'keepSnaps',
            loop: isMobile,
            watchDrag: preventDragWhilePinchZoomed,
        }, setApi: setCarouselApi, children: _jsxs(Stack, { direction: "col", gap: 4, children: [_jsx(ProductMediaCarouselOverlays, { onPlayVideo: onPlayVideo, children: _jsx(CarouselContent, { children: mediaCollection.map((image, index) => (_jsx(CarouselSlot, { children: renderContent(image, index, alt) }, getMediaItemKey(image, index)))) }) }), _jsx(Box, { paddingInline: "5", children: _jsxs(Stack, { gap: 4, align: "center", justify: "between", children: [_jsx(CarouselSlidePagination, {}), _jsxs(Stack, { gap: 2, children: [_jsx(CarouselPrevious, {}), _jsx(CarouselNext, {})] })] }) })] }) }));
};
const ProductMediaCarouselOverlays = ({ children, onPlayVideo }) => {
    const { mediaCollection, labelsConfig } = useProductMedia();
    const { isShopLanding } = useAemConfig();
    const collectionContainsVideos = useMemo(() => mediaCollection.some(m => m.isVideo), [mediaCollection]);
    const selectedColorId = useSelector(makeGetSelectedColorId());
    const { productCode, productId } = useSelector(makeGetProductDefaults());
    const selectedSku = useSelector(makeGetSelectedSku());
    const wishListProduct = {
        colorId: selectedColorId,
        productCode: productCode,
        productId: parseInt(productId, 10),
        sku: selectedSku,
    };
    const handlePlayVideo = () => {
        const firstVideoIndex = mediaCollection.findIndex(m => m.isVideo);
        if (firstVideoIndex !== -1) {
            onPlayVideo(firstVideoIndex);
        }
    };
    return (_jsxs(Grid, { testId: "product_slider", columns: "stack", gap: 0, children: [children, _jsx(Grid.Cell, { display: "flex", justify: "start", align: "end", children: _jsx(Box, { paddingBlockEnd: "2", flex: { grow: 0 }, children: _jsx(LabelsOverlay, Object.assign({}, labelsConfig, { testId: "product_slider_image_overlay" })) }) }), !isShopLanding && (_jsx(Grid.Cell, { display: "flex", justify: "end", align: "start", children: _jsx(Box, { paddingBlockStart: "5", paddingInlineEnd: "5", children: _jsx(ProductMediaWishListButton, { wishListProduct: wishListProduct }) }) })), collectionContainsVideos && (_jsx(Grid.Cell, { display: "flex", justify: "end", align: "end", children: _jsx(Box, { paddingBlockEnd: "2", paddingInlineEnd: "5", children: _jsx(PlayVideoButton, { onClick: handlePlayVideo, size: "sm" }) }) }))] }));
};
